import { EllipsisVerticalIcon, PlusIcon } from '@heroicons/react/20/solid';
import Page from '../../../../components/PageWrapper';
import { useEffect, useMemo, useState } from 'react';
import {
  changeDiscountCodeStatus,
  deleteDiscountCode,
  getAllDiscounts
} from '../../../../api/requests';
import { Discount } from '../../../../models/discount/Discount';
import Loader from '../../../../components/Loader';
import GTable from '../../../../components/elements/GTable';
import moment from 'moment';
import Button from '../../../../components/form-control/Button';
import {
  getDiscountRelationsOptionsForTable,
  getFormattedDate,
  showToast
} from '../../../../utils/miscFunctions';
import { ITablePaginationData, ITableRowAction } from '../../../../components/elements/GTable/GTable';
import PopOnMe from '../../../../components/elements/poponme';
import useModal from '../../../../hooks/modalHook';
import DiscountStatus from '../../../../components/elements/DiscountStatus';
import useDiscount from '../../../../hooks/discountHook';

interface VehiclesProps {
  isSettings?: boolean;
}

const Discounts = ({ isSettings = false }: VehiclesProps): JSX.Element => {
  const [discounts, setDiscounts] = useState<any>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const { ModalComponent, updateModalStates, closeModal } = useModal();
  const [searchString, setSearchString] = useState<string>('');
  const [discountsPaginationData, setDiscountsPaginationData] = useState<ITablePaginationData>();
  const [showPerPage, setShowPerPage] = useState<number>(20);
  const [filterData, setFilterData] = useState<any>();
  const { showAddDiscount, DiscountModal } = useDiscount();


  useEffect(() => {
    getDiscounts();
  }, []);

  useEffect(() => {
    getDiscounts(0);
  }, [showPerPage]);

  const getDiscounts = (page = 0) => {
    setLoading(true);
    getAllDiscounts(page, showPerPage)
      .then((response: any) => {
        const _discounts = response?.data?.content?.map((_discount: any) => {
          return new Discount(_discount);
        });
        setDiscounts(_discounts);
        const from = response?.data?.pageable?.offset + 1;
        const to =
          from + response?.data?.size > response?.data?.totalElements
            ? response?.data.totalElements
            : from + response?.data?.size - 1;

        setDiscountsPaginationData({
          per_page: response?.data?.size,
          total: response?.data?.totalElements,
          totalPages: response?.data?.totalPages,
          from: from,
          to: to
        });
      })
      .catch((error: Error) => { })
      .finally(() => {
        setLoading(false);
      });
  };

  const changeDiscountStatus = (id: number, status: string) => {
    changeDiscountCodeStatus(id, status)
      .then((response: any) => {
        setLoading(true);
        showToast.success(response.message ?? 'Discount code status changed succesfully');
        getDiscounts(0);
      })
      .catch((error: any) => { })
      .finally(() => {
        setLoading(false);
      });
  };

  const deleteDiscount = (id: number) => {
    deleteDiscountCode(id)
      .then((response: any) => {
        setLoading(true);
        showToast.success('Discount code deleted succesfully');
        getDiscounts(0);
      })
      .catch((error: any) => { })
      .finally(() => {
        setLoading(false);
      });
  };

  const getTableRowActions = (data: any): ITableRowAction[] => {
    const actions = [
      {
        label: 'Delete',
        callback: (discount: Discount) => {
          if (discount && !discount?.active) deleteDiscount(discount?.id);
          else {
            showToast.error('Only inactve codes can be deleted');
          }
        },
        data: data
      },
      {
        label: `${data?.active == false ? 'Activate' : 'Disable'}`,
        callback: (discount: Discount) => {
          let id = discount?.id;
          let status = discount?.active ? 'DEACTIVATE' : 'ACTIVATE';
          changeDiscountStatus(id, status);
        },
        data: data
      }
    ];

    return actions;
  };

  const columns = useMemo(
    () => [
      {
        Header: 'M',
        columns: [
          {
            Header: 'Name',
            accessor: 'name',
            Cell: (row: any) => {
              return <span>{row.cell.value ? row.cell.value : '-'}</span>;
            }
          },
          {
            Header: 'Code',
            accessor: 'code',
            Cell: (row: any) => {
              return <span>{row.cell.value ? row.cell.value : '-'}</span>;
            }
          },
          {
            Header: 'Type',
            accessor: 'type',
            Cell: (row: any) => {
              return <span>{row.cell.value ? row.cell.value : '-'}</span>;
            }
          },
          {
            Header: 'Value',
            accessor: 'value',
            Cell: (row: any) => {
              return <span>{row.cell.value ? row.cell.value : '-'}</span>;
            }
          },
          {
            Header: 'Applies To',
            accessor: 'relation',
            Cell: (row: any) => {
              return (
                <span>
                  {row.cell.value ? (
                    <>{getDiscountRelationsOptionsForTable(row.cell.value)}</>
                  ) : (
                    '-'
                  )}
                </span>
              );
            }
          },

          {
            Header: 'Expiry Date',
            accessor: 'expiresOn',
            Cell: (row: any) => {
              return (
                <span>{row.cell.value ? moment(row.cell.value).format('DD/MM/YYYY') : '-'}</span>
              );
            }
          },
          {
            Header: 'Status',
            accessor: 'active',
            Cell: (row: any) => {
              return <DiscountStatus status={row.cell.value} />;
            }
          },

          {
            Header: 'Created On',
            accessor: 'createdOn',
            Cell: (row: any) => {
              return <span>{row.cell.value ? getFormattedDate({ date: row.cell.value, showTime: true }) : '-'}</span>;
            }
          },
          {
            Header: 'Updated On',
            accessor: 'updatedOn',
            Cell: (row: any) => {
              return (
                <span>{row.cell.value ? getFormattedDate({ date: row.cell.value, showTime: true }) : '-'}</span>
              );
            }
          },
          {
            Header: 'Action',
            id: 'Action',
            Cell: (row: any) => {
              return (
                <div className="cursor-pointer flex justify-center">
                  <PopOnMe actions={getTableRowActions(row.row.original)}>
                    <EllipsisVerticalIcon className="h-4 w-4" />
                  </PopOnMe>
                </div>
              );
            }
          }
        ]
      }
    ],
    []
  );

  const handlePageChange = (page: number) => {
    getDiscounts(page);
  };

  const getData = () => {
    if (discounts && filterData?.search) {
      return (
        discounts &&
        discounts?.filter((discount: Discount) =>
          discount?.name?.toLowerCase()?.includes(filterData?.search)
        )
      );
    } else {
      return discounts;
    }
  };

  return (
    <Page title="Discount">
      <div className='py-5'>
        {loading ? (
          <Loader message="Please wait..." brollyLoaderFixed />
        ) : (<div className="bg-white dark:bg-darkBg w-full min-h-full">
          {!isSettings &&
            <div className=" flex justify-between items-center">
              <div className=" flex justify-end items-center">
                <div className=" flex gap-4">
                  <div className="">
                    <Button
                      onClick={showAddDiscount}
                      icon={<PlusIcon width="20px" height="20px" className="mr-2" />}
                      label="New Discount"
                      className="p-1  rounded"
                      bgColor="primary"
                    />
                  </div>
                </div>
              </div>
            </div>}



          <div className="">
            <GTable
              selectionChanged={(selection: any) => {
              }}
              columns={columns}
              data={getData()}
              classes="h-fit"
              isRowLinked={false}
              loading={loading}
              onSearch={(searchString: string) => setSearchString(filterData?.search)}
              searchPlaceholder={'Search by name...'}
              tableType={'discounts'}
              paginationData={discountsPaginationData}
              onPageChange={handlePageChange}
              showPerPage={showPerPage}
              onShowPerPageChange={(page: number) => setShowPerPage(page)}
              showDateRangeSelector
            />
          </div>

        </div>
        )}
      </div>
      <ModalComponent />
      <DiscountModal />
    </Page>
  );
};
export default Discounts;
